import {
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material';
import React from 'react';
import { P, match } from 'ts-pattern';
import TablerIconCopy from '~icons/tabler/copy';
import { MarkdownPreview } from '#/components/Utils';
import CollapseCard from '#/components/cards/collapse-card';
import { useNotify } from '#/lib/notify';
import { styleVars } from '#/utils/styles';
import type { ChatMessagePartToolBaseProps } from './types';

export type ChatMessagePartToolDocGeneratedProps =
  ChatMessagePartToolBaseProps<'generated-doc'>;

function ChatMessagePartToolDocGenerated({
  toolInvocation,
  onEngage,
}: ChatMessagePartToolDocGeneratedProps) {
  const theme = useTheme();
  const notify = useNotify();

  const result = React.useMemo(() => {
    const defaultResult = {
      title: 'New Document',
      summary: 'This is a new document',
      content: null,
      template: null,
      error: null,
    };

    return match(toolInvocation)
      .returnType<{
        title: string;
        summary: string;
        content: string | null;
        template: string | null;
        error: string | null;
      }>()
      .with({ state: 'result', error: P.string }, (x) => ({
        ...defaultResult,
        error: x.error,
      }))
      .otherwise((x) => {
        if (x.state === 'result') {
          return {
            ...(x.result ?? defaultResult),
            error: null,
          };
        }
        return defaultResult;
      });
  }, [toolInvocation]);

  const handleCopy = React.useCallback(() => {
    if (!result) {
      return;
    }
    navigator.clipboard.writeText(result.content ?? '');
    notify.send({
      message: 'Copied to clipboard',
      severity: 'success',
    });
    onEngage?.('copy', result);
  }, [onEngage, result]);

  if (Object.hasOwn(result, 'error') && result.error) {
    return null;
  }

  return (
    <CollapseCard
      variant='elevation'
      className='overflow-hidden'
      onToggle={() => onEngage?.('engage')}
      defaultCollapsed
    >
      <CardHeader
        title={
          <Stack
            component='span'
            direction='row'
            alignItems='center'
            spacing={1}
          >
            <span>{result.title}</span>
            {result.template && (
              <Tooltip
                title={`Generated using the "${result.template}" template`}
                placement='top'
                arrow
              >
                <Chip
                  className='cursor-pointer'
                  size='xsmall'
                  shape='rounded'
                  label={result.template}
                />
              </Tooltip>
            )}
          </Stack>
        }
        subheader={result.summary}
        action={
          <IconButton size='small' onClick={handleCopy}>
            <TablerIconCopy />
          </IconButton>
        }
      />
      <CardContent>
        {match(result.content)
          .with(P.string, (content) => <MarkdownPreview markdown={content} />)
          .otherwise(() => null)}
      </CardContent>
      {toolInvocation.state !== 'result' && (
        <LinearProgress
          className='z-(--chat-doc-z) absolute bottom-0 left-0 right-0'
          style={styleVars({
            '--chat-doc-z': `${theme.zIndex.appBar + 1}`,
          })}
        />
      )}
    </CollapseCard>
  );
}

export default React.memo(ChatMessagePartToolDocGenerated);
